import { ArrowDownRight, ArrowRight, ArrowUpRight } from "lucide-react";

export type CrmContactMomentumDirection = "accelerating" | "stable" | "decelerating";

export interface CrmContactHealthTrendPoint {
  compositeScore: number;
  computedAt: string;
}

interface CrmContactHeaderBadgesProps {
  leadScore?: number | null;
  healthScore?: number | null;
  momentumDirection?: CrmContactMomentumDirection | null;
}

interface CrmHeaderHealthSparklineProps {
  history: CrmContactHealthTrendPoint[];
  trendDirection?: CrmContactMomentumDirection | null;
  label?: string;
  emptyMessage?: string;
  historyDescription?: string;
}

type CrmHeaderScoreKind = "lead" | "health";
type CrmHeaderTone = "success" | "warning" | "destructive";

const SCORE_TONE_CLASSES: Record<CrmHeaderTone, string> = {
  success: "bg-[var(--color-success)]/15 text-[var(--color-success)] border-[var(--color-success)]/30",
  warning: "bg-[var(--color-warning)]/15 text-[var(--color-warning)] border-[var(--color-warning)]/30",
  destructive: "bg-[var(--color-destructive)]/15 text-[var(--color-destructive)] border-[var(--color-destructive)]/30",
};

export function CrmContactHeaderBadges({
  leadScore,
  healthScore,
  momentumDirection,
}: CrmContactHeaderBadgesProps) {
  return (
    <>
      {typeof leadScore === "number" && Number.isFinite(leadScore) ? (
        <CrmHeaderScoreBadge kind="lead" score={leadScore} />
      ) : null}
      {typeof healthScore === "number" && Number.isFinite(healthScore) ? (
        <CrmHeaderScoreBadge kind="health" score={healthScore} />
      ) : null}
      {momentumDirection ? <CrmHeaderMomentumBadge direction={momentumDirection} /> : null}
    </>
  );
}

export function CrmHeaderHealthSparkline({
  history,
  trendDirection,
  label = "90-day health",
  emptyMessage = "90-day health trend will appear once enough score snapshots exist.",
  historyDescription = "mounted score history",
}: CrmHeaderHealthSparklineProps) {
  if (history.length === 0) {
    return (
      <p className="text-xs text-[var(--color-muted-foreground)]">
        {emptyMessage}
      </p>
    );
  }

  const points = history
    .map((entry, index) => `${(index / Math.max(history.length - 1, 1)) * 120},${40 - (normalizeCrmHeaderScore(entry.compositeScore) / 100) * 40}`)
    .join(" ");
  const latest = history[history.length - 1];
  const earliest = history[0];
  const delta = normalizeCrmHeaderScore(latest?.compositeScore ?? 0) - normalizeCrmHeaderScore(earliest?.compositeScore ?? 0);
  const tone = getCrmHeaderMomentumColor(trendDirection);

  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">{label}</p>
          <p className="text-xs text-[var(--color-muted-foreground)]">
            {delta >= 0 ? "+" : ""}{delta} pts across {historyDescription}
          </p>
        </div>
        <svg viewBox="0 0 120 40" className="h-10 w-[120px]" aria-hidden="true">
          <polyline
            fill="none"
            stroke={tone}
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            points={points}
          />
        </svg>
      </div>
    </div>
  );
}

export function getCrmHeaderScoreTone(kind: CrmHeaderScoreKind, score: number): CrmHeaderTone {
  const normalized = normalizeCrmHeaderScore(score);
  if (kind === "lead") {
    if (normalized >= 60) return "success";
    if (normalized >= 30) return "warning";
    return "destructive";
  }
  if (normalized >= 70) return "success";
  if (normalized >= 40) return "warning";
  return "destructive";
}

export function normalizeCrmHeaderScore(score: number): number {
  if (!Number.isFinite(score)) return 0;
  return Math.max(0, Math.min(100, Math.round(score)));
}

function CrmHeaderScoreBadge({ kind, score }: { kind: CrmHeaderScoreKind; score: number }) {
  const label = kind === "lead" ? "Lead" : "Health";
  const title = kind === "lead" ? "Lead score" : "Health score";
  const normalized = normalizeCrmHeaderScore(score);
  const tone = getCrmHeaderScoreTone(kind, normalized);

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${SCORE_TONE_CLASSES[tone]}`}
      title={`${title}: ${normalized}/100`}
    >
      {label} {normalized}
    </span>
  );
}

function CrmHeaderMomentumBadge({ direction }: { direction: CrmContactMomentumDirection }) {
  const config = getCrmHeaderMomentumConfig(direction);
  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${config.color}`}
      title={`Momentum: ${config.label.toLowerCase()}`}
    >
      <Icon className="h-3 w-3" />
      {config.label}
    </span>
  );
}

function getCrmHeaderMomentumConfig(direction: CrmContactMomentumDirection) {
  if (direction === "accelerating") {
    return {
      label: "Rising",
      icon: ArrowUpRight,
      color: SCORE_TONE_CLASSES.success,
    };
  }
  if (direction === "decelerating") {
    return {
      label: "Slipping",
      icon: ArrowDownRight,
      color: SCORE_TONE_CLASSES.destructive,
    };
  }
  return {
    label: "Holding",
    icon: ArrowRight,
    color: SCORE_TONE_CLASSES.warning,
  };
}

function getCrmHeaderMomentumColor(direction?: CrmContactMomentumDirection | null): string {
  if (direction === "accelerating") return "var(--color-success)";
  if (direction === "decelerating") return "var(--color-destructive)";
  return "var(--color-warning)";
}
